import { useTranslation } from "react-i18next";
import KPIDisplay from "@/components/controls/KPIDisplay";
import { cn } from "@/utils/utils";
import useBoundStore from "@/services/store/BoundStore";
import { selectAccountCurrency } from "@/utils/numbers";
import { KPIDisplayProps } from "@/components/controls/kpiDisplayTypes";

interface Props {
  kpis: KPIDisplayProps[];
  isLoading?: boolean;
  className?: string;
}

const KPIBar = ({ kpis, isLoading, className }: Props) => {
  const { t } = useTranslation();
  const accountCurrency = useBoundStore(selectAccountCurrency);
  const displayKpis: KPIDisplayProps[] = isLoading
    ? Array.from({ length: 5 }, () => ({
        title: "",
        kpi: 0,
        isCurrency: false
      }))
    : kpis;

  const lastKpi = displayKpis[displayKpis.length - 1];
  const lastKpiHasMultipleArrayItems =
    Array.isArray(lastKpi?.kpiArray) && lastKpi.kpiArray.length > 1;

  // When last KPI spans 2 columns, use custom grid template where last 2 columns are 80% of normal
  const getGridColsClass = () => {
    if (displayKpis.length === 5 && lastKpiHasMultipleArrayItems) {
      return "grid-cols-[1fr_1fr_1fr_1fr_0.8fr_0.8fr]";
    }
    return displayKpis.length === 5 ? "grid-cols-6" : "grid-cols-4";
  };

  return (
    <div className="flex flex-row justify-center" data-test="kpi-bar-container">
      <div className="grow-1" />
      <div
        className={cn(
          "grid auto-cols-fr gap-3 lg:gap-5 bg-surface-elevation0 py-4 px-0 max-w-[1700px] grow-[2] items-start",
          getGridColsClass(),
          className
        )}
        data-test="kpi-bar"
      >
        {displayKpis.map((kpi, index) => {
          // Check if this is the last KPI (YTD Retention) which should span 2 columns
          // Only span 2 columns if the last KPI has an array with more than 1 element
          const isLastKpi = index === displayKpis.length - 1;
          const hasMultipleArrayItems =
            Array.isArray(kpi.kpiArray) && kpi.kpiArray.length > 1;
          const spanClass =
            isLastKpi && hasMultipleArrayItems ? "col-span-2" : "col-span-1";

          return (
            <div
              key={index}
              className={spanClass}
              data-test={`kpi-bar-item-${index}`}
            >
              <KPIDisplay
                isLoading={isLoading}
                {...kpi}
                currencyCode={kpi.currencyCode || accountCurrency}
                getKpiLabel={(key) => t(`kpis.${key}`)}
              />
            </div>
          );
        })}
      </div>
      <div className="grow-1" />
    </div>
  );
};

export default KPIBar;
